import { memo, useCallback, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import type { ExpectedField, ImportBatchRow, RowChangeSet } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { RowNumberBadge } from './ImportRowBadge';
import { useRowNumberDisplay } from './rowNumberDisplay';
import { ReviewCellMessage } from './ReviewCellMessage';
import {
  deletesChangeSet,
  differingFieldKeys,
  keepRowMessage,
  restoresChangeSet,
  reviewColumnLabel,
  REVIEW_SCROLL_MAX_HEIGHT_CLASS,
} from './reviewGridDraft';
import type { DuplicateGroup, ExactCopy } from './reviewTabs';

const EXACT_COPIES_HEADING = 'Removed automatically · exact copies';
const HIDDEN_ROWS_HEADING = 'Removed automatically · hidden in the sheet';
const MIXED_HEADING = 'Removed automatically · exact copies and hidden rows';
const HIDDEN_NOTE = 'Hidden in the sheet';
const NO_USERNAME_TEXT = 'No username';
const EMPTY_TEXT = 'No duplicates left';
const DIFFERS_CLASS = 'bg-background-warning-light-hover';
const CELL_CLASS = 'max-w-48 p-2 text-body-sm-regular text-foreground';
const ERROR_CELL_CLASS = 'bg-destructive/10';
const ERROR_ICON_SIZE = 14;
// A 5,000-row sheet can hold thousands of groups; drawing them all costs
// ~100k DOM nodes and a multi-second render on every save.
export const DUPLICATE_GROUPS_PAGE_SIZE = 25;
export const EXACT_COPIES_PAGE_SIZE = 50;

export interface DuplicatesTabProps {
  groups: DuplicateGroup[];
  exactCopies: ExactCopy[];
  fields: ExpectedField[];
  /** Decides the identity column's name: Username or Channel. */
  campaignPlatform: string;
  busy: boolean;
  onSaveRows: (changes: RowChangeSet, message: string) => void;
}

type SaveRows = DuplicatesTabProps['onSaveRows'];

function hasErrors(row: ImportBatchRow): boolean {
  return Object.keys(row.validationErrors).length > 0;
}

function removedHeading(copies: ExactCopy[]): string {
  const hidden = copies.filter((copy) => copy.reason === 'hidden_in_sheet').length;
  if (hidden === 0) return EXACT_COPIES_HEADING;
  return hidden === copies.length ? HIDDEN_ROWS_HEADING : MIXED_HEADING;
}

function removedRowName(copy: ExactCopy): string {
  return copy.row.usernameKey || copy.row.values.username || NO_USERNAME_TEXT;
}

function groupHeading(group: DuplicateGroup): string {
  return `${group.usernameKey} · ${group.rows.length} rows · pick the one to keep`;
}

function exactCopyNote(copy: ExactCopy, show: (rowNumber: number) => number): string {
  if (copy.reason === 'hidden_in_sheet') return HIDDEN_NOTE;
  if (copy.copyOfRowNumber === null) return 'Exact copy of a removed row';
  return `Exact copy of row ${show(copy.copyOfRowNumber)}`;
}

interface GroupCellProps {
  row: ImportBatchRow;
  field: ExpectedField;
  differs: boolean;
}

// Same as the grid's error cell: the whole cell is tinted, and the reason is
// reachable by hover, keyboard focus and screen readers, not a hover-only title.
function GroupCell({ row, field, differs }: GroupCellProps) {
  const value = row.values[field.key] ?? '';
  const error = row.validationErrors[field.key];
  if (error === undefined) {
    return <td className={cn(CELL_CLASS, 'truncate', differs && DIFFERS_CLASS)}>{value}</td>;
  }
  return (
    <td className={cn(CELL_CLASS, ERROR_CELL_CLASS)}>
      <ReviewCellMessage message={error}>
        <span tabIndex={0} className="flex min-w-0 items-center gap-1.5">
          <WarningCircle size={ERROR_ICON_SIZE} aria-hidden="true" className="shrink-0 text-destructive" />
          <span className="truncate">{value}</span>
        </span>
      </ReviewCellMessage>
    </td>
  );
}

interface GroupRowProps {
  row: ImportBatchRow;
  group: DuplicateGroup;
  fields: ExpectedField[];
  differing: Set<string>;
  busy: boolean;
  onSaveRows: SaveRows;
}

function GroupRow({ row, group, fields, differing, busy, onSaveRows }: GroupRowProps) {
  const blocked = hasErrors(row);
  const title = blocked ? `Fix row ${row.rowNumber} first` : undefined;

  function keep() {
    const removed = group.rows.filter((other) => other.rowNumber !== row.rowNumber).map((other) => other.rowNumber);
    onSaveRows(deletesChangeSet(removed), keepRowMessage(row.rowNumber, removed));
  }

  return (
    <tr className="border-t border-border">
      <th scope="row" className="p-2 text-left font-normal">
        <RowNumberBadge n={row.rowNumber} />
      </th>
      {fields.map((field) => (
        <GroupCell key={field.key} row={row} field={field} differs={differing.has(field.key)} />
      ))}
      <td className="p-2 text-right">
        <span title={title} className="inline-flex">
          <Button type="button" variant="outline" size="xs" aria-label={`Keep this row ${row.rowNumber}`} title={title} disabled={busy || blocked} onClick={keep}>
            Keep this row
          </Button>
        </span>
      </td>
    </tr>
  );
}

interface GroupBlockProps {
  group: DuplicateGroup;
  fields: ExpectedField[];
  campaignPlatform: string;
  busy: boolean;
  onSaveRows: SaveRows;
}

// Paging is what bounds the work. The memo only spares the visible blocks a
// re-render when the tab re-renders for a reason that leaves a block's props
// as they were (its group object changes on every save, as rows are reloaded).
const GroupBlock = memo(function GroupBlock({ group, fields, campaignPlatform, busy, onSaveRows }: GroupBlockProps) {
  const heading = groupHeading(group);
  const differing = differingFieldKeys(group.rows, fields);
  return (
    <section aria-label={heading} tabIndex={-1} className="overflow-x-auto rounded-lg border border-border bg-card outline-none">
      <p className="px-3 pt-3 pb-2 text-body-sm-medium text-foreground">{heading}</p>
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left text-caption-medium text-muted-foreground">
            <th scope="col" className="px-2 py-1 font-medium">Row</th>
            {fields.map((field) => (
              <th key={field.key} scope="col" className="px-2 py-1 font-medium">{reviewColumnLabel(field, campaignPlatform)}</th>
            ))}
            <th scope="col" className="px-2 py-1">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {group.rows.map((row) => (
            <GroupRow key={row.rowNumber} row={row} group={group} fields={fields} differing={differing} busy={busy} onSaveRows={onSaveRows} />
          ))}
        </tbody>
      </table>
    </section>
  );
});

/** Moves focus to the first item a page just added, once the Show more
 *  button that held it has gone (the last page). */
function useFocusFirstAdded(list: RefObject<HTMLElement | null>, selector: string) {
  const from = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (from.current === null) return;
    const target = list.current?.querySelectorAll<HTMLElement>(selector)[from.current];
    from.current = null;
    target?.focus();
  });
  return (firstAddedIndex: number) => {
    from.current = firstAddedIndex;
  };
}

function ShowMore({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm" className="self-center" onClick={onClick}>
      {label}
    </Button>
  );
}

function ExactCopies({ copies, busy, onSaveRows }: { copies: ExactCopy[]; busy: boolean; onSaveRows: SaveRows }) {
  const show = useRowNumberDisplay();
  const headingId = useId();
  const [limit, setLimit] = useState(EXACT_COPIES_PAGE_SIZE);
  const shown = copies.slice(0, limit);
  const listRef = useRef<HTMLUListElement>(null);
  const focusFirstAdded = useFocusFirstAdded(listRef, 'li');
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h4 id={headingId} className="text-caption-medium text-muted-foreground">{removedHeading(copies)}</h4>
      <ul ref={listRef} className="flex flex-col divide-y divide-border rounded-lg border border-border bg-card">
        {shown.map((copy) => (
          <li key={copy.row.rowNumber} tabIndex={-1} className="flex items-center gap-3 px-3 py-2 text-body-sm-regular outline-none">
            <RowNumberBadge n={copy.row.rowNumber} />
            <span className="min-w-0 flex-1 truncate">{removedRowName(copy)}</span>
            <span className="text-muted-foreground">{exactCopyNote(copy, show)}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-label={`Restore row ${copy.row.rowNumber}`}
              disabled={busy}
              onClick={() => onSaveRows(restoresChangeSet([copy.row.rowNumber]), `Row ${copy.row.rowNumber} restored`)}
            >
              Restore
            </Button>
          </li>
        ))}
      </ul>
      {copies.length > shown.length && (
        <ShowMore
          label={`Show ${Math.min(EXACT_COPIES_PAGE_SIZE, copies.length - shown.length)} more of ${copies.length - shown.length} remaining`}
          onClick={() => {
            if (copies.length - shown.length <= EXACT_COPIES_PAGE_SIZE) focusFirstAdded(shown.length);
            setLimit((n) => n + EXACT_COPIES_PAGE_SIZE);
          }}
        />
      )}
    </section>
  );
}

export function DuplicatesTab({ groups, exactCopies, fields, campaignPlatform, busy, onSaveRows }: DuplicatesTabProps) {
  const [limit, setLimit] = useState(DUPLICATE_GROUPS_PAGE_SIZE);
  const shown = groups.slice(0, limit);
  const listRef = useRef<HTMLDivElement>(null);
  const focusFirstAdded = useFocusFirstAdded(listRef, 'section[aria-label]');
  // The blocks are memoised, so they get a callback whose identity never
  // changes; it reads the current prop through a ref.
  const latestSave = useRef(onSaveRows);
  useLayoutEffect(() => {
    latestSave.current = onSaveRows;
  });
  const saveRows = useCallback<SaveRows>((changes, message) => latestSave.current(changes, message), []);
  return (
    <div ref={listRef} className={cn('flex flex-col gap-4 overflow-y-auto', REVIEW_SCROLL_MAX_HEIGHT_CLASS)}>
      {groups.length === 0 && exactCopies.length === 0 && <p className="py-8 text-center text-body-sm-regular text-muted-foreground">{EMPTY_TEXT}</p>}
      {shown.map((group) => (
        <GroupBlock key={group.usernameKey} group={group} fields={fields} campaignPlatform={campaignPlatform} busy={busy} onSaveRows={saveRows} />
      ))}
      {groups.length > shown.length && (
        <ShowMore
          label={`Show ${Math.min(DUPLICATE_GROUPS_PAGE_SIZE, groups.length - shown.length)} more of ${groups.length - shown.length} remaining groups`}
          onClick={() => {
            if (groups.length - shown.length <= DUPLICATE_GROUPS_PAGE_SIZE) focusFirstAdded(shown.length);
            setLimit((n) => n + DUPLICATE_GROUPS_PAGE_SIZE);
          }}
        />
      )}
      {exactCopies.length > 0 && <ExactCopies copies={exactCopies} busy={busy} onSaveRows={onSaveRows} />}
    </div>
  );
}
